import { useEffect, useMemo, useState } from 'react';
import './about-stats.scss';
import type { AboutStatItem } from '../../../interfaces/about';

interface AboutStatsProps {
  title?: string;
  description?: string;
  items?: AboutStatItem[];
  data?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export function AboutStats({ title, description, items, data }: AboutStatsProps) {
  const statItems = useMemo(
    () =>
      (items ?? (data ?? [])).map((item) => ({
        value: String(item.value ?? ''),
        label: String(item.key ?? ''),
      })),
    [items, data]
  );

  const formatLabel = (label: string) =>
    label
      ? label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()
      : '';

  const [displayValues, setDisplayValues] = useState<string[]>(
    statItems.map((item) => item.value)
  );

  useEffect(() => {
    setDisplayValues(statItems.map(() => '0'));

    const rafIds: number[] = [];

    statItems.forEach((item, index) => {
      const rawValue = item.value;
      const targetNumber = Number(rawValue.replace(/[^0-9.-]+/g, ''));

      if (!Number.isFinite(targetNumber)) {
        setDisplayValues((prev) => {
          const next = [...prev];
          next[index] = rawValue;
          return next;
        });
        return;
      }

      const duration = 900;
      const startTime = performance.now();

      const step = (time: number) => {
        const progress = Math.min((time - startTime) / duration, 1);
        const current = Math.round(targetNumber * progress);
        setDisplayValues((prev) => {
          const next = [...prev];
          next[index] = String(current);
          return next;
        });

        if (progress < 1) {
          rafIds.push(requestAnimationFrame(step));
        }
      };

      rafIds.push(requestAnimationFrame(step));
    });

    return () => {
      rafIds.forEach((id) => cancelAnimationFrame(id));
    };
  }, [statItems]);

  return (
    <section className="about-stats">
      {(title || description) && (
        <div className="about-stats-header">
          {title && <h3 className="about-stats-title">{title}</h3>}
          {description && <p className="about-stats-description">{description}</p>}
        </div>
      )}

      <div className="about-stats-list">
        {statItems.map((item, index) => (
          <article
            className="about-stats-item"
            key={`${item.label || item.value || index}`}
          >
            <div className="about-stats-content">
              <p className="about-stats-value">{displayValues[index] ?? item.value}</p>
              <p className="about-stats-label">{formatLabel(item.label)}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
